import styled, { css } from 'styled-components';

import { Cta } from 'core/components/Cta/Cta.style';
import CtaCmp from 'core/components/Cta';
import { FONT_NAMES, FONT_WEIGHT } from 'core/styles/fonts';
import * as mx from 'core/styles/mixins';
import { displaySmall } from 'core/styles/Typography/Typography.style';

import { Ul as UlBase } from '../AdditionalLinksGroup/AdditionalLinksGroup.style';

export const Wrapper = styled.div<{
	isMenuOpen?: boolean;
	mainHeaderContentHeight?: number;
	isIOS?: boolean; // For future iOS stylings
}>`
	width: 100%;
	background-color: ${({ theme }) =>
		theme.global.page.authenticatedHeader.color};
	.mobile--header--collapse-wrapper {
		width: inherit;
	}

	${({ isMenuOpen, mainHeaderContentHeight }) =>
		isMenuOpen &&
		css`
			height: calc(
				100vh - ${mainHeaderContentHeight}px - env(safe-area-inset-bottom)
			);
			overflow-y: scroll;

			/* Safari only */
			@media not all and (min-resolution: 0.001dpcm) {
				@media {
					padding-bottom: 10vh;
				}
			}
		`}
`;

export const Ul = styled(UlBase as any)`
	width: 100%;
	margin-bottom: 32px;

	> li {
		&.active {
			color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.subItems.fontColor};
			background-color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.subItems.color};
			border-left: 3px solid
				${({ theme }) => theme.global.page.authenticatedHeader.activeBar.color};
			-webkit-text-stroke-width: 1px;
		}
	}

	&&& > li a {
		padding: 12px 21px;

		i {
			opacity: 1;
		}
	}
`;

export const Nav = styled.nav<{
	showAccountSwitcher: boolean;
}>`
	${displaySmall}

	width: 100%;
	margin-bottom: 0;
	border-top: 1px solid #dde5ed;
	background-color: ${({ theme }) =>
		theme.global.page.authenticatedHeader.color};
	margin-top: 0;
`;

export const PrimaryNavListLi = styled.li<{
	isLevel2Expanded?: boolean;
}>`
	${({ isLevel2Expanded }) =>
		isLevel2Expanded &&
		css`
			background-color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.subItems.mobile.iaSelectedColor};
			position: absolute;
			top: 55px;
			left: 0;
			width: 100%;
			z-index: 1;
		`}
`;

export const PrimaryNavList = styled.ul`
	${mx.listReset}

	padding: 0;
	margin: 0 0 32px;
	width: 100%;
	text-align: left;

	&&& > li + li {
		margin-top: 0;
		height: 100%;
	}

	> li {
		display: block;
		text-align: left;
		border-bottom: 1px ${({ theme }) => theme.global.borders.hr} solid;
		height: 56px;

		/* Menu item */
		a,
		${/* sc-selector */ Cta} button span {
			${mx.buttonReset}

			width: 100%;
			text-align: start;

			display: block;
			text-decoration: none;

			color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.primary.mobile.fontColor};
			font-size: 20px;
			font-family: ${FONT_NAMES.OPEN_SANS};
			font-weight: ${FONT_WEIGHT.BOOK};
			padding: 16px 20px;
			border: none;
			background-color: inherit;

			&:hover,
			&:focus {
				cursor: pointer;
				background-color: ${({ theme }) =>
					theme.global.page.authenticatedHeader.primary.mobile.iaHoverColor};
			}
		}

		${Cta} {
			width: 100%;
			display: block;

			button {
				margin: 0;
				padding: 0;
				width: 100%;
				border: unset;
				background-color: inherit;
			}
		}

		&:first-of-type {
			margin-top: 0;
		}

		&:last-child {
			border-bottom: 4px
				${({ theme }) => theme.global.page.authenticatedHeader.activeBar.color}
				solid;
		}
	}
`;

export const PrimaryNavListCta = styled(CtaCmp)<{
	isExpanded: boolean;
}>`
	&:hover,
	&:focus {
		cursor: pointer;
		background-color: ${({ theme }) =>
			theme.global.page.authenticatedHeader.primary.mobile.iaHoverColor};
	}

	color: ${({ isExpanded, theme }) =>
		isExpanded &&
		theme.global.page.authenticatedHeader.subItems.mobile.arrowBack};

	/* Submenu Indicator Icon */
	i {
		${({ isExpanded }) =>
			(isExpanded &&
				css`
					float: left;
					color: ${({ theme }) =>
						theme.global.page.authenticatedHeader.subItems.mobile.arrowBack};
				`) ||
			css`
				float: right;
				color: ${({ theme }) =>
					theme.global.page.authenticatedHeader.caret.color};
			`}
	}
`;

export const SubNavList = styled.ul`
	${mx.listReset}

	padding: 0;
	border-top: 1px ${({ theme }) => theme.global.borders.hr} solid;
	margin: 0;

	li {
		text-align: left;
		padding: 15px 20px 15px 52px;
		height: 56px;
		border-bottom: 1px solid ${({ theme }) => theme.global.borders.lightest};
		margin-top: 0;

		& + & {
			margin-top: 16px;
		}

		&.-active {
			color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.subItems.fontColor};
			background-color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.subItems.mobile.iaHoverColor};
		}

		:hover::before {
			width: 100%;
			height: 100%;
			bottom: 0;
			top: 0;
			left: 0;
			background-color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.primary.mobile.iaHoverColor};
		}

		a {
			display: block;
			text-decoration: none;
			color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.subItems.fontColor};
			font-family: 'Open Sans';
			font-size: 16px;
			letter-spacing: 0;
			font-weight: ${FONT_WEIGHT.LIGHT};
			padding: 0;
		}

		&:last-child {
			border-bottom: unset;
		}

		&:first-of-type {
			margin-top: 0;
		}

		&:hover {
			cursor: pointer;
			background-color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.primary.mobile.iaHoverColor};
		}
	}
`;

export const ToggleButton = styled.button`
	${mx.buttonReset}
	${mx.tabFocus}

	display: block;
	position: absolute;
	right: 26px;
	top: 10px;

	> span {
		display: inherit;
		padding: 4px;

		i {
			display: inherit;
			color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.hamburgerButton.color};
		}
	}

	&:hover {
		cursor: pointer;
	}
`;

export const AccountSwitcherMobile = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	padding: 10px 25px 10px 30px;
	height: 44px;
	width: 100%;
	font-size: 12px;
	line-height: 20px;
	letter-spacing: 0;
	font-weight: ${FONT_WEIGHT.SEMI_BOLD};
	color: ${({ theme }) => theme.global.page.authenticatedHeader.color};
	background-color: ${({ theme }) => theme.global.colors.secondary.color};

	&:hover {
		cursor: pointer;
	}
`;

export const AccountSwitcherContent = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;

	label:nth-child(2) {
		margin-left: 5px;
	}
`;
